// Auto-generated from docs/architecture/orchestrator.md — do not edit manually
const content = `
# Orchestrator Architecture

**Date:** 2026-03-01
**Scope:** Architecture of the multi-agent coordination layer -- from API request handling through agent negotiation to results persistence.

---

## Table of Contents

1. [Overview](#1-overview)
2. [Architecture](#2-architecture)
3. [API Routes](#3-api-routes)
4. [Orchestration Engine (Cloud Run Service)](#4-orchestration-engine-cloud-run-service)
5. [Rolling Orchestrator (flux-core Simulation Engine)](#5-rolling-orchestrator-flux-core-simulation-engine)
6. [Agent Integration](#6-agent-integration)
7. [ADMM Coordination](#7-admm-coordination)
8. [Meta-Controller](#8-meta-controller)
9. [Results Pipeline](#9-results-pipeline)
10. [Key Files](#10-key-files)

---

## 1. Overview

The Orchestrator coordinates multi-agent optimization across battery storage (Site DNA), EV fleet charging, and cold storage refrigeration. These heterogeneous agents share a single utility meter and transformer, creating coupling through demand charges and capacity constraints.

There are two distinct orchestration layers in the system:

1. **Cloud Run Service** (\`apps/vortx-orchestrator/\`): A Flask API that receives optimization requests, loads configurations from Firestore, runs iterative constraint-exchange negotiation between backend services (Site DNA API, Flux EV API), and persists results. This is the original multi-app coordination layer for cross-service optimization.

2. **flux-core RollingOrchestrator** (\`libs/python/flux-core/src/flux_core/simulation/orchestrator.py\`): An in-process simulation engine that runs 35,040 quarter-hour timesteps comparing siloed vs. orchestrated agent operation. Uses ADMM or Column Generation coordination at day boundaries, with intra-day Gauss-Seidel refinement. This is the core of the Flux EMS annual simulation and real-time digital twin.

The Cloud Run service handles request routing and persistence. The RollingOrchestrator handles the actual simulation math. The Flux EMS API (\`apps/flux-ems-api/\`) bundles flux-core and calls \`RollingOrchestrator\` directly for annual simulations.

### System Position

\`\`\`
Frontend (Flux EMS Web)
    │
    ▼
Platform API (auth + routing)
    │
    ├── Orchestrator API (Cloud Run) ──── cross-service constraint exchange
    │       │
    │       ├── Site DNA API
    │       ├── Flux EV API
    │       └── Firestore (results)
    │
    └── Flux EMS API (Cloud Run) ──────── in-process annual simulation
            │
            └── flux-core RollingOrchestrator
                    │
                    ├── SiteDNAAgent (LP / HiGHS)
                    ├── EVFleetAgent (LP / HiGHS)
                    └── ColdStorageAgent (MILP / PuLP+HiGHS)
\`\`\`

---

## 2. Architecture

### Cloud Run Service Architecture

The \`vortx-orchestrator\` Cloud Run service is a Flask application that orchestrates optimization across separate backend APIs. It communicates with Site DNA API and Flux EV API via HTTP and exchanges constraint envelopes between iterations.

**Request flow:**

1. Frontend sends \`POST /optimize\` with Firebase auth token and project/scenario IDs.
2. \`app.py\` verifies the token via Firebase Admin SDK, parses the request into an \`OptimizationRequest\`.
3. \`OrchestrationEngine.optimize()\` loads configurations from Firestore (Site DNA scenario + Flux EV project).
4. The engine runs an iterative negotiation loop:
   - Flux EV optimizes first (unconstrained).
   - Site DNA optimizes with Flux EV's load curve as a constraint.
   - Flux EV re-optimizes with Site DNA's capacity limits.
   - Cold Storage (optional) optimizes with remaining capacity.
   - Repeat until convergence or max iterations.
5. \`ResultAggregator\` combines per-app results into a unified \`OptimizationResult\`.
6. Results are saved to Firestore (\`simulation_runs\` collection with config snapshots).

**Convergence detection** uses four strategies:
- Primary: objective changes < 1% between iterations
- Minimal change: objective changes < 0.1% (early stop)
- Oscillation: alternating between two solutions (4-iteration window)
- Divergence: 3+ consecutive increases in objective value

### In-Process Simulation Architecture

The \`RollingOrchestrator\` in flux-core is where the actual multi-agent coordination math runs. It operates in two modes:

**Siloed mode:** Each agent optimizes independently, tracking only its own monthly peak. No inter-agent coordination. Results may exceed transformer capacity (violations are tracked).

**Orchestrated mode:** Agents coordinate via ADMM or Column Generation. At each of the 35,040 timesteps:

1. Build 48-hour forecast window (192 quarter-hour steps).
2. Agents publish forecasts to \`SharedContext\`.
3. Round 1 (baseline): each agent proposes independently.
4. Coordination (day-boundary): full consensus ADMM (50 iterations) or CG.
5. Coordination (intra-day): Gauss-Seidel with DC + congestion prices.
6. Do-no-harm check: keep best iteration (lowest system peak).
7. Hard capacity enforcement via marginal-value allocation if total > transformer limit.
8. Apply with actual conditions + capacity constraints.
9. Update running monthly peaks (system-wide and per-agent).

---

## 3. API Routes

All routes on the Cloud Run service require Firebase ID token authentication via \`Authorization: Bearer <token>\` header.

### \`GET /health\`

Health check for load balancers. Returns service status, version, and timestamp.

### \`POST /optimize\`

Run multi-app optimization with iterative negotiation.

**Coordination format (current):**
\`\`\`json
{
    "site_dna": {
        "project_id": "abc123",
        "scenario_id": "scenario-456"
    },
    "flux_ev": {
        "project_id": "xyz789",
        "config_id": "config-abc"
    },
    "objectives": {
        "primary": {"type": "cost", "weight": 1.0, "convergence_threshold": 0.01}
    },
    "mode": "normal",
    "max_iterations": 10
}
\`\`\`

**Legacy format (backward compatible):**
\`\`\`json
{
    "scenario_id": "abc123",
    "project_id": "xyz789",
    "primary_objective": {"type": "cost", "weight": 1.0}
}
\`\`\`

Returns \`OptimizationResult\` with combined Site DNA, Flux EV, and Cold Storage results plus iteration log.

### \`GET /runs\`

List user's simulation runs. Query params:
- \`include_archived\` (bool, default false)
- \`limit\` (int, default 50)

### \`GET /runs/<run_id>\`

Get simulation run details and results.

### \`PUT /runs/<run_id>\`

Update simulation run metadata (name, description, archive status).

### \`DELETE /runs/<run_id>\`

Delete or archive simulation run. Query params:
- \`hard_delete\` (bool, default false): permanently delete vs. soft archive.

---

## 4. Orchestration Engine (Cloud Run Service)

**File:** \`apps/vortx-orchestrator/services/orchestration_engine.py\`

The \`OrchestrationEngine\` class coordinates optimization across backend service APIs through iterative constraint exchange.

### Initialization

The engine holds references to service clients (injected at startup):
- \`firestore\`: \`FirestoreService\` for loading configs and saving results.
- \`site_dna_client\`: HTTP client to Site DNA API.
- \`flux_ev_client\`: HTTP client to Flux EV API.
- \`cold_storage_client\`: Optional HTTP client to Cold Storage API.
- \`digital_twin_client\`: Optional physics-based topology extraction.

### Negotiation Flow

\`\`\`
Iteration 1: Flux EV → optimize(no constraints)
                        ↓
Iteration 2: Site DNA → optimize(EV load as additional_load_kw)
             Flux EV  → optimize(Site DNA transformer/export limits)
             Cold     → optimize(remaining capacity after Site DNA + EV)
                        ↓
Iteration N: ... repeat until convergence or max_iterations
\`\`\`

### Constraint Builder

**File:** \`apps/vortx-orchestrator/services/constraint_builder.py\`

Translates one app's outputs into another's input constraints via \`ConstraintEnvelope\`:

| Direction | Constraints Exchanged |
|---|---|
| Flux EV -> Site DNA | \`additional_load_kw\` (depot load curve) |
| Site DNA -> Flux EV | \`transformer_limits_kw\`, \`export_limits_kw\`, \`energy_prices_usd_kwh\`, \`demand_charge_usd_kw\` |
| Site DNA + Flux EV -> Cold Storage | \`available_capacity_kw\` (transformer limit minus EV load), \`energy_prices_usd_kwh\`, \`demand_charge_usd_kw\` |

When a \`site_topology\` is available from the Digital Twin client, transformer limits are additionally capped by the physical transformer capacity.

### Firestore Service

**File:** \`apps/vortx-orchestrator/services/firestore_service.py\`

Database: \`vortx-flux-site-dna\` (named Firestore database).

**Collections:**
- \`users/{uid}/projects/{pid}/scenarios/{sid}\` -- Site DNA scenarios
- \`users/{uid}/ev_projects/{pid}\` -- Flux EV projects
- \`users/{uid}/simulation_runs/{run_id}\` -- Simulation run results with config snapshots
- \`users/{uid}/orchestration_runs/{run_id}\` -- Legacy orchestration results

Simulation runs store full config snapshots (both Site DNA and Flux EV configs at run time) so results remain reproducible even if source projects change later.

---

## 5. Rolling Orchestrator (flux-core Simulation Engine)

**File:** \`libs/python/flux-core/src/flux_core/simulation/orchestrator.py\`

The \`RollingOrchestrator\` class is the core simulation engine. It runs a Model Predictive Control (MPC) loop over 35,040 quarter-hour timesteps for annual simulation.

### Constructor

\`\`\`python
RollingOrchestrator(
    agents: List[SimAgent],
    site_capacity_kw: float,
    max_iterations: int = 5,
    demand_charge_usd_kw: float = 0.0,
)
\`\`\`

Agents are \`SimAgent\` instances (in-process, not HTTP clients). The orchestrator owns their lifecycle -- calling \`reset()\`, \`propose()\`, and \`apply()\` each timestep.

### MPC Loop (Orchestrated Mode)

**Method:** \`run_orchestrated(forecast_qh, actual_qh, prices_qh, coordination_mode, coordination_config)\`

At each timestep \`t\` (0..35039):

1. **Month boundary check**: Reset monthly peaks, global LP cache, and ADMM warm-start state at the start of each month.

2. **Build forecast**: 192-step (48-hour) rolling window of ambient temperatures, energy prices, PV generation, and building load.

3. **SharedContext assembly**: Each agent publishes an \`AgentForecast\` (planned horizon, committed load blocks, flexibility bounds). These are collected into a \`SharedContext\` that all agents see during \`propose()\`.

4. **Baseline round**: Each agent proposes independently with its own monthly peak and full demand charge. This establishes the siloed-equivalent baseline.

5. **Coordination** (day-boundary, \`step_in_day == 0\`):
   - Build \`ConsensusADMMState\` instances from demand charge config.
   - Run \`consensus_admm_coordinate()\` for up to \`admm_max_iters\` iterations.
   - Store z-targets and rho for intra-day congestion pricing.
   - Do-no-harm: if ADMM peak > baseline peak, fall back to baseline.

6. **Coordination** (intra-day, \`step_in_day > 0\`):
   - Pass 1: All agents get full DC + congestion prices derived from ADMM z-targets.
   - Pass 2: Battery agent re-proposes seeing actual EV/Cold power via \`other_agents_power_kw\`. Uses ADMM step-0 peak as the monthly peak floor (prevents cold drift ratchet).
   - Intra-day do-no-harm: fall back to baseline if Pass 2 worsens peak.

7. **Hard capacity enforcement**: If total demand exceeds transformer capacity, \`allocate_by_marginal_value()\` distributes capacity to agents ranked by marginal value ($/kW). Higher-value agents get priority. Generators are unconstrained and free up capacity.

8. **Apply**: Each agent commits its action with actual conditions. Power consumption, energy cost, SOC, and generation are recorded.

9. **Peak tracking**: System monthly peak, per-agent monthly peaks, and per-hour system peaks are updated.

### Siloed Mode

**Method:** \`run_siloed(forecast_qh, actual_qh, prices_qh)\`

Same loop structure but with no inter-agent coordination. Each agent sees only its own monthly peak. For annual runs, demand charge is amortized by days remaining in the month (progressive incentive ramp-up). Violations are tracked but not enforced.

### Capacity Allocation

**Function:** \`allocate_by_marginal_value(proposals, capacity_kw)\`

When total demand exceeds transformer capacity:
- Generators (negative power) are unconstrained and free up capacity.
- Consumers are sorted by \`marginal_value_per_kw\` (highest first).
- Available capacity = transformer + net generation.
- Each consumer receives up to its requested power until capacity is exhausted.

### Global LP (Benchmark)

**Method:** \`_solve_daily_global_lp()\`

Runs a centralized global LP (\`solve_global_optimum()\`) for one day as a theoretical benchmark. Cached by day index, cleared at month boundaries. Extracts agent configurations via duck-typing (battery SOC from \`SiteDNAAgent\`, vehicles/chargers from \`EVFleetAgent\`, facility config from \`ColdAgent\`).

---

## 6. Agent Integration

---
*This page is auto-generated from \`docs/architecture/\`. See the full document for complete details.*
`;

export default content;
